import { useEffect, useState } from 'react';
import AdvantagesSlider from './AdvantagesSlider';
import LimitationsSlider from './LimitationsSlider';
import { Header } from '@/components/header';
import { Footer } from '@/components/footer';
import Cta from '@/components/cta';
import { FAQ } from '@/components/faq';
import { Partners } from '@/components/partners';
import { useTranslation } from "react-i18next";
import { t } from 'i18next';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { HowItWorks } from '@/components/HowItWorks';
import { ICONS } from '@/assets/icons';

type Feature = {
  icon: any;
  title: string;
  
  desc: string;
};

export const advantages: Feature[] = [
  {
    icon: ICONS.earlyDetection,
    title: t("models.fireSmokeDetection.details.advantages.title1"),
    desc: t("models.fireSmokeDetection.details.advantages.description1")
  },
  {
    icon: ICONS.reduceDamage,
    title: t("models.fireSmokeDetection.details.advantages.title2"),
    desc: t("models.fireSmokeDetection.details.advantages.description2")
  },
  {
    icon: ICONS.easyIntegration,
    title: t("models.fireSmokeDetection.details.advantages.title3"),
    desc: t("models.fireSmokeDetection.details.advantages.description3")
  },
  {
    icon: ICONS.twentyFourSevenProtection,
    title: t("models.fireSmokeDetection.details.advantages.title4"),
    desc: t("models.fireSmokeDetection.details.advantages.description4")
  }
];

export const limitations: Feature[] = [
  {
    icon: ICONS.vapeCigaretteOrPuff,
    title: t("models.fireSmokeDetection.details.limitations.title1"),
    desc: t("models.fireSmokeDetection.details.limitations.description1")
  },
];

const whatIs: Feature[] = [
  {
    icon: ICONS.definition,
    title: t("models.fireSmokeDetection.details.whatIs.definitionTitle"),
    desc: t("models.fireSmokeDetection.details.whatIs.definitionDescription")
  },
  {
    icon: ICONS.fonctionnalite,
    title: t("models.fireSmokeDetection.details.whatIs.functionalityTitle"),
    desc: t("models.fireSmokeDetection.details.whatIs.functionalityDescription")
  },
  {
    icon: ICONS.importance,
    title: t("models.fireSmokeDetection.details.whatIs.importanceTitle"),
    desc: t("models.fireSmokeDetection.details.whatIs.importanceDescription")
  },
];

const impactAndBenefits: Feature[] = [
  {
    icon: ICONS.reductionDesPertes,
    title: t("models.fireSmokeDetection.details.impactAndBenefits.title1"),
    desc: t("models.fireSmokeDetection.details.impactAndBenefits.description1")
  },
  {
    icon: ICONS.securiteProactive,
    title: t("models.fireSmokeDetection.details.impactAndBenefits.title2"),
    desc: t("models.fireSmokeDetection.details.impactAndBenefits.description2")
  },
  {
    icon: ICONS.scalabilite,
    title: t("models.fireSmokeDetection.details.impactAndBenefits.title3"),
    desc: t("models.fireSmokeDetection.details.impactAndBenefits.description3")
  },
];

const howItWorks = [
  {
    title: t("models.fireSmokeDetection.details.howItWorks.title1"),
    desc: t("models.fireSmokeDetection.details.howItWorks.description1"),
    icon: ICONS.analyseTempsReel
  },
  {
    title: t("models.fireSmokeDetection.details.howItWorks.title2"),
    desc: t("models.fireSmokeDetection.details.howItWorks.description2"),
    icon: ICONS.traitementAvance
  },
  {
    title: t("models.fireSmokeDetection.details.howItWorks.title3"),
    desc: t("models.fireSmokeDetection.details.howItWorks.description3"),
    icon: ICONS.alertsInstantanees
  },
];

const FireModelDetails = () => {
  const { t } = useTranslation();
  const [imgInView, setImgInView] = useState(false);
  const [stepInView, setStepInView] = useState([false, false, false]);

  useEffect(() => {
    setImgInView(true);
    const timers = howItWorks.map((_, i) =>
      setTimeout(() => setStepInView((prev) => {
        const newState = [...prev];
        newState[i] = true;
        return newState;
      }), i * 300)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <>
      <Header />
      {/* Hero Section */}

      <div id="hero" className="relative h-screen w-full overflow-hidden">
        {/* Background GIF with Overlay */}
        <div className="absolute inset-0 z-0">
          {/* Blue gradient overlays for cinematic darkening */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#001a33]/80 via-[#001a33]/60 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,0,0,0.35)_0%,_rgba(0,0,0,0.65)_100%)]" />
          <img
            src="./Fire & smoke banner.png"
            alt="Violence detection visual"
            className={`w-full h-auto object-cover rounded-xl shadow-2xl transition-all duration-700 ease-in-out ${imgInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto sm:mx-0 text-white px-6 sm:pl-6 sm:pr-0 md:pl-6 lg:pl-6 xl:pl-6 text-center sm:text-left">
              <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-korolev font-bold leading-tight tracking-wide">
                <span className="block pt-3 sm:pt-4 md:pt-5 text-primary-200/90 tracking-wider">
                  {t('models.fireSmokeDetection.details.title')}
                </span>
              </h1>
              <h2 className="hidden sm:block mt-3 sm:mt-5 text-lg sm:text-2xl md:text-2xl font-korolev font-medium text-gray-100 max-w-2xl">
                {t('models.fireSmokeDetection.details.description')}
              </h2>


              <div className="mt-6 sm:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4 font-poppins">
                <Link href="/free-trial" className="group relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <Button
                    size="lg"
                    className="w-full sm:w-auto bg-white text-black hover:bg-gray-50 hover:scale-[1.02] px-4 py-4 sm:px-6 sm:py-6 text-sm sm:text-lg font-medium transition-all duration-300 hover:shadow-lg group-hover:shadow-primary/20"
                  >
                    <span className="relative z-10">
                      {t('models.requestDemo')}
                    </span>
                  </Button>
                </Link>
                <Link
                  href="/contact-sales"
                  className="group relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto bg-transparent border-white/70 text-white hover:bg-white/10 hover:border-white/90 hover:scale-[1.02] px-4 py-4 sm:px-6 sm:py-6 text-sm sm:text-lg font-medium transition-all duration-300 hover:shadow-lg group-hover:shadow-white/10"
                  >
                    <span className="relative z-10">{t('models.contactUs')}</span>
                  </Button>
                </Link>
              </div>

            </div>
          </div>
        </div>
        {/* Scroll Indicator */}
        <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 opacity-40">
          <button
            onClick={() => {
              window.scrollTo({
                top: window.innerHeight,
                behavior: 'smooth'
              });
            }}
            aria-label="Scroll down"
            className="flex flex-col items-center text-white hover:text-primary-200 transition-colors group"
          >
            <span className="text-sm font-medium mb-2 opacity-80 group-hover:opacity-100 transition-opacity">
              {t('hero.scrollDown', 'Scroll down')}
            </span>
            <div className="w-10 h-16 rounded-full border-2 border-white/50 flex items-start justify-center p-1 group-hover:border-white transition-colors">
              <div className="w-1 h-4 bg-white rounded-full animate-bounce" />
            </div>
          </button>
        </div>
      </div>
      {/* What Is Section - Timeline Style */}
      {/* What Is Section - Card Style */}
      <section className="mt-5 mb-0 py-16 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-4 md:px-8 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-extrabold mb-12 tracking-tight text-center  relative z-10">{t("models.fireSmokeDetection.details.whatIs.title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {whatIs.map((item, i) => (
              <div key={i} className="flex flex-col items-center bg-white dark:bg-slate-800 rounded-2xl border-2 border-slate-200 dark:border-slate-700 p-8 hover:shadow-lg transition-shadow">
                {/* Icon */}
                <div className="w-16 h-16 rounded-xl bg-slate-600 dark:bg-slate-600 flex items-center justify-center text-white text-2xl mb-6">
                  <img className='w-10 h-10'src={item.icon} alt={item.title} />
                </div>
                {/* Title */}
                <h3 className="font-bold text-lg text-slate-900 dark:text-white text-center mb-4">
                  {item.title}
                </h3>
                {/* Description */}
                <p className="text-sm text-slate-600 dark:text-slate-300 text-center leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* How It Works Section (Enhanced Horizontal Stepper) */}
      <HowItWorks
        title={t('models.fireSmokeDetection.details.howItWorks.title')}
        steps={howItWorks}
        stepInView={stepInView}
      />

      {/* Impact and Benefits Section - Timeline Style (Inverted) */}
      <section className="py-16 bg-slate-800 dark:bg-slate-900 text-white px-4 md:px-8 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-extrabold mb-12 tracking-tight text-center relative z-10">
            {t("models.fireSmokeDetection.details.impactAndBenefits.title")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {impactAndBenefits.map((item, i) => (
              <div
                key={i}
                className="flex flex-col items-center bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 hover:shadow-lg hover:bg-white/10 transition-all"
              >
                {/* Icon */}
                <div className="w-16 h-16 rounded-xl bg-white/10 flex items-center justify-center text-white text-2xl mb-6">
                  <img className='w-10 h-10' src={item.icon} alt="Icon" />
                </div>
                {/* Title */}
                <h3 className="font-bold text-lg text-white text-center mb-4">
                  {item.title}
                </h3>
                {/* Description */}
                <p className="text-sm text-slate-200 text-center leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages and Limitations Section */}
      <section className="mt-24 mb-34 py-20 bg-gradient-to-b from-white to-slate-50 dark:from-slate-900 dark:to-slate-800 text-slate-900 dark:text-white px-4 md:px-8 lg:px-16 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-black/5 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-black/5 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3"></div>
        <div className="max-w-7xl mx-auto relative z-20">
        <h2 className="text-3xl md:text-4xl font-extrabold mb-16 text-center dark:text-white text-slate-900">
            {t("models.advantages&Limitations")}
          </h2>
          <div className="flex flex-col lg:flex-row gap-16 items-stretch w-full">
            {/* Advantages Slider */}
            <div className="flex-1 flex flex-col min-w-0 max-w-full h-full">
              <h3 className="text-2xl font-bold mb-4 text-left pl-4 border-l-4 border-slate-900 dark:border-white">
                {t("models.fireSmokeDetection.details.advantages.title")}
              </h3>
              <div className="flex-1 flex flex-col h-full min-h-[260px]">
                <AdvantagesSlider
                  itemClassName="group flex items-center gap-4 py-4 px-6 border-b border-slate-200 dark:border-slate-700 last:border-0 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-all duration-300 min-h-[90px]"
                />
              </div>
            </div>
            {/* Limitations Slider */}
            <div className="flex-1 flex flex-col min-w-0 max-w-full h-full">
              <h3 className="text-2xl font-bold mb-4 text-left pl-4 border-l-4 border-slate-900 dark:border-white">
                {t("models.fireSmokeDetection.details.limitations.title")}
              </h3>
              <div className="flex-1 flex flex-col h-full min-h-[260px]">
                <LimitationsSlider
                  itemClassName="group flex items-center gap-4 py-4 px-6 border-b border-slate-200 dark:border-slate-700 last:border-0 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-all duration-300 min-h-[90px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Partners />
      <Cta />
      <div className="mt-20 mb-20 mx-5 md:mx-0">
        <FAQ faqs={t('models.fireSmokeDetection.details.faq', { returnObjects: true }) as Array<{ id?: string, question: string, answer: string }>} title={t('faq.title')} subtitle={t('models.fireSmokeDetection.faqDescription')} />
      </div>
      <Footer />
    </>
  );
};

export default FireModelDetails;